import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, effect, inject, input, Input, OnInit, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ResumeType } from 'src/app/enums/resume-type';
import { TravelAvailability } from 'src/app/enums/travel-availability';
import { CarouselComponent } from 'src/app/layouts/carousel/carousel.component';
import { AddResume, AddResumeDocument, AddResumeEducation } from 'src/app/models/resume/add-resume';
import { DropDownKeywords, DropdownOption } from 'src/app/models/shared/dropdown-option';
import { DropdownService } from 'src/app/services/dropdown.service';
import { ResumeService } from 'src/app/services/resume.service';
import { CvEducationsComponent } from './cv-educations/cv-educations.component';
import { CvEmploymentsComponent } from "./cv-employments/cv-employments.component";
import { CvCertificationsComponent } from "./cv-certifications/cv-certifications.component";
import { CvDocumentsComponent } from "./cv-documents/cv-documents.component";
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';
import { CvReferencesComponent } from "./cv-references/cv-references.component";
import { CvSkillsComponent } from './cv-skills/cv-skills.component';
@Component({
  selector: 'app-create-cvcandidates',
  templateUrl: './create-cvcandidates.component.html',
  styles: [],
  standalone: true,
  imports: [CarouselComponent, CommonModule, ReactiveFormsModule, CvEducationsComponent, CvEmploymentsComponent, CvCertificationsComponent, CvDocumentsComponent, CvReferencesComponent, CvSkillsComponent]
})
export class CreateCVCandidatesComponent implements OnInit {
  public resumeId = input<number | null>(null);
  private readonly resumeService = inject(ResumeService);
  private readonly router = inject(Router);
  private readonly dropdownService = inject(DropdownService);
  private readonly _toastrService = inject(ToastrService);

  public form = new FormGroup({
    id: new FormControl(0),
    name: new FormControl('', [Validators.required, Validators.maxLength(1000)]),
    description: new FormControl('', [Validators.maxLength(2000)]),
    isEnabled: new FormControl(true),
    isPublic: new FormControl(false),
    isDefault: new FormControl(false),
    templateId: new FormControl<number | null>(null,[Validators.required]),
    typeId: new FormControl(ResumeType.WEB, Validators.required),
    travelAvailability: new FormControl(TravelAvailability.Not, [Validators.required, Validators.min(1), Validators.max(3)]),
    salaryExpectationId: new FormControl<number | null>(null),
    workPermit: new FormControl(false),
    publishResumeAsFreelancer: new FormControl<boolean | null>(false),
    declareInformationIsTrue: new FormControl(false),
    consentForSelectionProcess: new FormControl(false),
    templatePreviewId: new FormControl<number | null>(null),
    chargeTypeId: new FormControl<number | null>(null),
    amountToChargePerType: new FormControl<number | null>(null)
  });

  salaryExpectationOptions: DropdownOption[] = [];
  resumeTemplatesOptions: DropdownOption[] = []
  chargeTypeOptions: DropdownOption[] = [];

  isLoading = signal<boolean>(false);

  cvEducationsComponent = viewChild(CvEducationsComponent);
  cvSkillsComponent = viewChild(CvSkillsComponent);
  cvEmploymentsComponent = viewChild(CvEmploymentsComponent);
  cvCertificationsComponent = viewChild(CvCertificationsComponent);
  cvDocumentsComponent = viewChild(CvDocumentsComponent);
  cvReferencesComponent = viewChild(CvReferencesComponent);

  API_URL = environment.api.url;

  selectedTab = signal<string>('tabcv-01-tab-pane');

  constructor() {
    effect(() => {
      if (this.resumeId()) {
        this.getResume();
      }
    })
  }

  ngOnInit(): void {
    this.getOptions();
  }

  getResume() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.resumeService.getResumeById(resumeId).subscribe({
      next: res => {
        const resume = res;
        this.form.patchValue({
          id: resume.id,
          name: resume.name,
          description: resume.description,
          isEnabled: resume.isEnabled,
          isPublic: resume.isPublic,
          isDefault: resume.isDefault,
          templateId: resume.templateId,
          templatePreviewId: resume.templatePreviewId,
          typeId: resume.typeId,
          travelAvailability: resume.travelAvailability,
          salaryExpectationId: resume.salaryExpectationId,
          workPermit: resume.workPermit,
          publishResumeAsFreelancer: resume.publishResumeAsFreelancer,
          declareInformationIsTrue: resume.declareInformationIsTrue,
          consentForSelectionProcess: resume.consentForSelectionProcess,
          chargeTypeId: resume.chargeTypeId,
          amountToChargePerType: resume.amountToChargePerType
        });
      }, error: (error: string[]) => {
        this.router.navigate(['candidates/my-cvs']);
          Swal.fire({
            title: 'Error',
            text: error[0],
            icon: 'error',
            confirmButtonColor: '#d33'
          });
      }
    })
  }

  getOptions() {
    this.dropdownService.GetOptions(DropDownKeywords.SalaryExpectations).subscribe({
      next: res => {
        this.salaryExpectationOptions = res;
      }
    });
    this.dropdownService.GetOptions(DropDownKeywords.ResumeTemplates).subscribe({
      next: res => {
        this.resumeTemplatesOptions = res;
      }
    });
    this.dropdownService.GetOptions(DropDownKeywords.ChargeTypes).subscribe({
      next: res => {
        this.chargeTypeOptions = res;
      }
    });
  }

  selectTemplate(template: DropdownOption) {
    this.form.get('templateId')?.setValue(template.id);
    if(template?.code){
      this.form.get('templatePreviewId')?.setValue(+template.code);
    }
  }

  reset() {
    if (this.resumeId()) {
      this.router.navigate(['candidates/my-cvs']);
      return;
    }
    this.form.patchValue({
      id: 0,
      name: null,
      description: null,
      isEnabled: true,
      isPublic: false,
      isDefault: false,
      templateId: null,
      templatePreviewId: null,
      typeId: ResumeType.WEB,
      travelAvailability: TravelAvailability.Not,
      salaryExpectationId: null,
      workPermit: false,
      publishResumeAsFreelancer: false,
      declareInformationIsTrue: false,
      consentForSelectionProcess: false,
      chargeTypeId: null,
      amountToChargePerType: null
    });
  }

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    const resume = this.form.getRawValue() as any as AddResume;

    this.resumeService.addResume({
      ...resume,
      educations: !resume.id ? this.cvEducationsComponent()?.educations() : [],
      skills: !resume.id && this.cvSkillsComponent() ? [
        ...(this.cvSkillsComponent()?.skills() ?? []),
        ...(this.cvSkillsComponent()?.softSkills() ?? [])
      ] : [],
      languages: !resume.id ? this.cvSkillsComponent()?.languages() : [],
      employments: !resume.id ? this.cvEmploymentsComponent()?.employments() : [],
      certifications: !resume.id ? this.cvCertificationsComponent()?.certifications() : [],
      documents: !resume.id ? this.cvDocumentsComponent()?.documents()?.filter(document => document.file).map(document => ({
        id: null,
        resumeId: null,
        resumeRequiredDocumentId: document.resumeRequiredDocumentId,
        fileId: null,
        file: document.file,
        fileName: document.fileName,
        isEnabled: true
      })) : [],
      references: !resume.id ? this.cvReferencesComponent()?.references() : []
    }).subscribe({
      next: res => {
        this.isLoading.set(false);
        if (res) {
          this.router.navigate(['candidates/my-cvs']);
        }
      },
      error: (error: string[]) => {
        this.isLoading.set(false);
        this._toastrService.error(error[0]);
      }
    });
  }

}
